/**
 * StandardsPicker
 *
 * Progressive filter UI for selecting an official academic standard.
 * Steps: Framework → Subject → Grade → Domain → Standard
 *
 * On selection, calls onSelect(standard) — the caller populates the form field.
 * The teacher can return to manual entry at any time via onManual().
 */
import React, { useState, useMemo } from 'react';
import { ChevronRight, ChevronLeft, Search, X, BookOpen, PenLine } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Standard, FrameworkMeta,
  getFrameworks, getSubjectsForFramework, getGradesForSubject,
  getDomainsForGrade, getStandardsForDomain,
} from './standards-data';

// ── Types ─────────────────────────────────────────────────────────────────────

type Step = 'framework' | 'subject' | 'grade' | 'domain' | 'standard';

interface Selection {
  framework?: FrameworkMeta;
  subject?: string;
  grade?: string;
  domainCode?: string;
  domainName?: string;
}

interface Props {
  onSelect: (standard: Standard) => void;
  onManual: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function gradeLabel(g: string): string {
  if (g === 'K') return 'Kindergarten';
  const n = Number(g);
  if (n === 1) return '1st Grade';
  if (n === 2) return '2nd Grade';
  if (n === 3) return '3rd Grade';
  return `${n}th Grade`;
}

function Breadcrumb({ sel, onBack }: { sel: Selection; onBack: (step: Step) => void }) {
  const parts: { label: string; step: Step }[] = [];
  if (sel.framework) parts.push({ label: sel.framework.value, step: 'framework' });
  if (sel.subject)   parts.push({ label: sel.subject,         step: 'subject' });
  if (sel.grade)     parts.push({ label: `Gr. ${sel.grade}`,  step: 'grade' });
  if (sel.domainName) parts.push({ label: sel.domainName,     step: 'domain' });

  if (parts.length === 0) return null;

  return (
    <div className="flex items-center gap-1 flex-wrap text-xs text-slate-500 pb-2 border-b border-slate-100">
      {parts.map((p, i) => (
        <React.Fragment key={p.step}>
          {i > 0 && <ChevronRight size={11} className="text-slate-300 flex-shrink-0" />}
          <button
            onClick={() => onBack(p.step)}
            className="hover:text-primary hover:underline transition-colors"
          >
            {p.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function StandardsPicker({ onSelect, onManual }: Props) {
  const [step, setStep]       = useState<Step>('framework');
  const [sel, setSel]         = useState<Selection>({});
  const [query, setQuery]     = useState('');

  // ── Hoist ALL hooks before any early returns (Rules of Hooks) ─────────────
  // allStandards is only meaningful on the 'standard' step; returns [] otherwise.
  const allStandards = useMemo(() => {
    if (step === 'standard' && sel.framework && sel.subject && sel.grade && sel.domainCode) {
      return getStandardsForDomain(sel.framework.value, sel.subject, sel.grade, sel.domainCode);
    }
    return [];
  }, [step, sel.framework, sel.subject, sel.grade, sel.domainCode]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return allStandards;
    return allStandards.filter(s =>
      s.code.toLowerCase().includes(q) ||
      s.wording.toLowerCase().includes(q),
    );
  }, [allStandards, query]);

  function goBack(toStep: Step) {
    const order: Step[] = ['framework', 'subject', 'grade', 'domain', 'standard'];
    const idx = order.indexOf(toStep);
    // Clear everything after the target step
    setSel(prev => {
      const next: Selection = { ...prev };
      if (idx <= 0) { delete next.framework; }
      if (idx <= 1) { delete next.subject; }
      if (idx <= 2) { delete next.grade; }
      if (idx <= 3) { delete next.domainCode; delete next.domainName; }
      return next;
    });
    setStep(toStep);
    setQuery('');
  }

  // ── Step: framework ──────────────────────────────────────────────────────
  if (step === 'framework') {
    const frameworks = getFrameworks();
    return (
      <PickerShell title="Select a standards framework" onManual={onManual}>
        <div className="space-y-2">
          {frameworks.map(f => (
            <button key={f.value} onClick={() => {
              setSel({ framework: f });
              setStep('subject');
            }}
              className="w-full text-left px-4 py-3 rounded-xl border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{f.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{f.description}</p>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-primary transition-colors flex-shrink-0" />
              </div>
            </button>
          ))}

          <button onClick={onManual}
            className="w-full text-left px-4 py-3 rounded-xl border border-dashed border-slate-200 hover:border-primary hover:bg-primary/5 transition-all group flex items-center gap-3">
            <PenLine size={16} className="text-slate-400 group-hover:text-primary flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-slate-700">Enter a custom or district standard</p>
              <p className="text-xs text-slate-400 mt-0.5">Type a standard code or custom learning target manually</p>
            </div>
          </button>
        </div>
      </PickerShell>
    );
  }

  // ── Step: subject ────────────────────────────────────────────────────────
  if (step === 'subject' && sel.framework) {
    const subjects = getSubjectsForFramework(sel.framework.value);
    return (
      <PickerShell title="Select a subject" onManual={onManual}
        back={<BackButton onClick={() => goBack('framework')} />}
        breadcrumb={<Breadcrumb sel={sel} onBack={goBack} />}
      >
        <div className="flex gap-2 flex-wrap">
          {subjects.map(s => (
            <button key={s} onClick={() => {
              setSel(prev => ({ ...prev, subject: s }));
              setStep('grade');
            }}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:border-primary hover:bg-primary/5 hover:text-primary transition-all"
            >
              {s}
            </button>
          ))}
        </div>
      </PickerShell>
    );
  }

  // ── Step: grade ──────────────────────────────────────────────────────────
  if (step === 'grade' && sel.framework && sel.subject) {
    const grades = getGradesForSubject(sel.framework.value, sel.subject);
    return (
      <PickerShell title="Select a grade" onManual={onManual}
        back={<BackButton onClick={() => goBack('subject')} />}
        breadcrumb={<Breadcrumb sel={sel} onBack={goBack} />}
      >
        <div className="flex gap-2 flex-wrap">
          {grades.map(g => (
            <button key={g} onClick={() => {
              setSel(prev => ({ ...prev, grade: g }));
              setStep('domain');
            }}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:border-primary hover:bg-primary/5 hover:text-primary transition-all"
            >
              {gradeLabel(g)}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-3 text-center">
          Pilot coverage — other grades not yet available. Use "Enter manually" for K–2 or 6–8.
        </p>
      </PickerShell>
    );
  }

  // ── Step: domain ─────────────────────────────────────────────────────────
  if (step === 'domain' && sel.framework && sel.subject && sel.grade) {
    const domains = getDomainsForGrade(sel.framework.value, sel.subject, sel.grade);
    return (
      <PickerShell title="Select a domain" onManual={onManual}
        back={<BackButton onClick={() => goBack('grade')} />}
        breadcrumb={<Breadcrumb sel={sel} onBack={goBack} />}
      >
        <div className="space-y-1.5">
          {domains.map(d => (
            <button key={d.code} onClick={() => {
              setSel(prev => ({ ...prev, domainCode: d.code, domainName: d.name }));
              setStep('standard');
              setQuery('');
            }}
              className="w-full text-left px-4 py-2.5 rounded-xl border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all group flex items-center justify-between"
            >
              <div>
                <span className="text-[11px] font-bold text-slate-400 font-mono mr-2">{d.code}</span>
                <span className="text-sm font-medium text-slate-700">{d.name}</span>
              </div>
              <ChevronRight size={15} className="text-slate-300 group-hover:text-primary flex-shrink-0" />
            </button>
          ))}
        </div>
      </PickerShell>
    );
  }

  // ── Step: standard ───────────────────────────────────────────────────────
  if (step === 'standard' && sel.framework && sel.subject && sel.grade && sel.domainCode) {
    return (
      <PickerShell title="Select a standard" onManual={onManual}
        back={<BackButton onClick={() => goBack('domain')} />}
        breadcrumb={<Breadcrumb sel={sel} onBack={goBack} />}
      >
        {allStandards.length > 5 && (
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search standards…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
            />
            {query && (
              <button onClick={() => setQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={13} />
              </button>
            )}
          </div>
        )}

        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-0.5">
          {filtered.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">No standards match your search.</p>
          )}
          {filtered.map(s => (
            <button key={s.code} onClick={() => onSelect(s)}
              className="w-full text-left px-4 py-3 rounded-xl border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all group"
            >
              <div className="flex items-start gap-2">
                <span className="text-[11px] font-bold text-primary font-mono mt-0.5 flex-shrink-0">{s.code}</span>
                <p className="text-xs text-slate-600 leading-snug group-hover:text-slate-800 transition-colors line-clamp-3">
                  {s.wording}
                </p>
              </div>
            </button>
          ))}
        </div>
      </PickerShell>
    );
  }

  return null;
}

// ── Shell & primitives ────────────────────────────────────────────────────────

function PickerShell({
  title, children, back, breadcrumb, onManual,
}: {
  title: string;
  children: React.ReactNode;
  back?: React.ReactNode;
  breadcrumb?: React.ReactNode;
  onManual: () => void;
}) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={title}
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="border border-slate-200 rounded-2xl bg-white shadow-sm p-4 space-y-3"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {back}
            <BookOpen size={14} className="text-primary flex-shrink-0" />
            <span className="text-sm font-semibold text-slate-800 truncate">{title}</span>
          </div>
          <button onClick={onManual}
            className="text-xs text-slate-400 hover:text-slate-600 flex-shrink-0 transition-colors whitespace-nowrap">
            Enter manually
          </button>
        </div>

        {breadcrumb}
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="flex items-center justify-center w-6 h-6 rounded-lg hover:bg-slate-100 transition-colors text-slate-500 flex-shrink-0">
      <ChevronLeft size={14} />
    </button>
  );
}

// ── Picked standard badge (shown in the form after selection) ─────────────────

export function PickedStandardBadge({
  standard,
  onClear,
}: {
  standard: Standard;
  onClear: () => void;
}) {
  return (
    <div className="flex items-start gap-2 px-3 py-2.5 bg-primary/5 border border-primary/20 rounded-xl">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] font-bold text-primary font-mono">{standard.code}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
            {standard.framework} {standard.version}
          </span>
        </div>
        <p className="text-xs text-slate-600 leading-snug mt-0.5 line-clamp-2">{standard.wording}</p>
      </div>
      <button onClick={onClear} aria-label="Clear selected standard"
        className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors mt-0.5">
        <X size={14} />
      </button>
    </div>
  );
}
